import React from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../lib/useSiteSettings';

const SIZES = {
    xs: { icon: 36, pad: '0.3rem 0.45rem', wordmark: false, wordmarkScale: 0.85 },
    sm: { icon: 44, pad: '0.35rem 0.5rem', wordmark: true, wordmarkScale: 0.92 },
    md: { icon: 56, pad: '0.4rem 0.55rem', wordmark: true, wordmarkScale: 1 },
    lg: { icon: 72, pad: '0.5rem 0.65rem', wordmark: true, wordmarkScale: 1.18 },
    xl: { icon: 88, pad: '0.55rem 0.7rem', wordmark: true, wordmarkScale: 1.35 },
};

function renderWordmark(platformName) {
    const custom = platformName?.trim();
    if (custom) {
        const match = custom.match(/^(.+?)(\.(ro|com))$/i);
        if (match) {
            return (
                <>
                    <span className="brand-logo-wordmark__name">{match[1]}</span>
                    <span className="brand-logo-wordmark__tld">{match[2].toLowerCase()}</span>
                </>
            );
        }
        return <span className="brand-logo-wordmark__name">{custom}</span>;
    }
    return (
        <>
            <span className="brand-logo-wordmark__name">CLICKMENIU</span>
            <span className="brand-logo-wordmark__tld">.RO</span>
        </>
    );
}

export function Logo({
    size = 'md',
    to = '/',
    className = '',
    onDark = true,
    showWordmark,
    logoUrl,
    platformName,
    layout = 'inline',
}) {
    // Auto-pick logo + name from live site settings unless explicitly overridden.
    // Without this, pages that don't fetch site settings (Login, Register,
    // LegalLayout, RestaurantLayout) keep the stale bundled /logo.png forever.
    const { settings: liveSite, platformName: liveName } = useSiteSettings();
    const effectiveLogoUrl = logoUrl ?? liveSite?.logo_url ?? null;
    const effectivePlatformName = platformName ?? liveName;
    const s = SIZES[size] || SIZES.md;
    const withWordmark = showWordmark ?? s.wordmark;
    const stacked = layout === 'stacked';

    const mark = effectiveLogoUrl ? (
        <span
            className={`brand-logo-mark${onDark ? ' brand-logo-mark--lift' : ''}${withWordmark ? ' brand-logo-mark--icon' : ''}`}
            style={{
                padding: s.pad,
                ...(withWordmark
                    ? { width: s.icon, height: s.icon }
                    : { height: s.icon }),
            }}
        >
            <img
                src={effectiveLogoUrl}
                alt=""
                style={{ height: withWordmark ? '100%' : s.icon, width: 'auto', display: 'block' }}
                data-testid="app-logo"
            />
        </span>
    ) : (
        <span
            className={`brand-logo-mark${withWordmark ? ' brand-logo-mark--icon' : ''}`}
            style={{
                padding: s.pad,
                ...(withWordmark
                    ? { width: s.icon, height: s.icon }
                    : { height: s.icon }),
            }}
            aria-hidden="true"
        />
    );

    const wordmark = withWordmark ? (
        <span className="brand-logo-wordmark" data-testid="platform-wordmark" aria-label={effectivePlatformName?.trim() || 'clickmeniu.ro'}>
            {renderWordmark(effectivePlatformName)}
        </span>
    ) : null;

    const content = stacked ? (
        <>
            {mark}
            {wordmark}
        </>
    ) : (
        <>
            {mark}
            {wordmark}
        </>
    );

    const logoStyle = { '--wordmark-scale': s.wordmarkScale };
    const logoClass = `brand-logo${stacked ? ' brand-logo--stacked' : ''} ${className}`.trim();

    if (to == null) {
        return (
            <span className={logoClass} style={{ display: 'inline-flex', alignItems: 'center', ...logoStyle }}>
                {content}
            </span>
        );
    }

    return (
        <Link
            to={to}
            className={logoClass}
            style={logoStyle}
            data-testid="logo-link"
        >
            {content}
        </Link>
    );
}
